import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { pipe } from 'fp-ts/lib/function'
import { Table, Select } from 'antd'
import { record, array, ord, string } from 'fp-ts'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { AssignableKeys, setAssignmentAction } from './types'
import { messageLookups } from './shared'
import MissingTags from './MissingTags'
import { useMode } from '../Bills/queries'

const useAssignmentState = () => {
  const upload = useSelector(s => s.upload)
  return upload.state === 'Assign' ? upload : undefined
}

type Props = { sheetName: string }

function invertRecord<A extends string, B extends string>(
  r: Record<A, B>
): Record<B, A> {
  const f = {} as Record<B, A>
  for (const k in r) {
    f[r[k]] = k
  }

  return f
}

const emptyAssignment = {
  amount: '',
  reference: '',
  createdAt: '',
  dueAt: '',

  payerEmail: '',
  payerAddress: '',
  payerTaxNumber: '',
  payerPhonenumber: '',
  payerName: '',

  discountName: '',
  discountRate: '',

  taxName: '',
  taxRate: '',

  organization: '',
  email: '',
}

const billKeys = (mode: 'organization' | 'supplier'): AssignableKeys[] =>
  mode === 'organization'
    ? [
        'amount',
        'reference',
        'createdAt',
        'dueAt',
        'payerEmail',
        'payerAddress',
        'payerPhonenumber',
        'payerName',
        'discountName',
        'discountRate',
        'taxName',
        'taxRate',
      ]
    : [
        'amount',
        'reference',
        'createdAt',
        'dueAt',
        'organization',
        'email',
        'discountName',
        'discountRate',
        'taxName',
        'taxRate',
      ]
const ordByTranslation = (intl: IntlShape): ord.Ord<AssignableKeys> =>
  pipe(
    string.Ord,
    ord.contramap(id => intl.formatMessage({ id }).toLowerCase())
  )

const AssignColumnsToSheet = ({ sheetName }: Props) => {
  const assignmentState = useAssignmentState()
  const assignments =
    assignmentState?.assignments?.[sheetName] ?? emptyAssignment
  const invertedAssignments = invertRecord(assignments)
  const data = pipe(
    assignmentState?.data?.[sheetName] ?? {},
    record.collect((col, value) => ({ col, value, key: value }))
  )
  const dispatch = useDispatch()
  const mode = useMode()
  const intl = useIntl()
  const targetColumns = React.useMemo(
    () =>
      pipe(
        billKeys(mode),
        array.sort(ordByTranslation(intl)),
        array.map(v => (
          <Select.Option value={v} key={v}>
            {messageLookups[v]}
          </Select.Option>
        ))
      ),
    [mode, intl]
  )

  return (
    <>
      <MissingTags assignments={assignments} />
      <Table
        size="small"
        dataSource={data}
        columns={[
          {
            title: <FormattedMessage id="sourceColumn" />,
            dataIndex: 'col',
          },
          {
            title: <FormattedMessage id="sampleValues" />,
            dataIndex: 'value',
            render: (_, c) => (
              <>
                {pipe(
                  c.value,
                  array.takeLeft(1),
                  array.map(v => (
                    <>
                      {v.toString()}
                      <br />
                    </>
                  ))
                )}
              </>
            ),
          },
          {
            title: <FormattedMessage id="targetColumn" />,
            dataIndex: 'col',
            render: (_, c) => (
              <Select<AssignableKeys>
                bordered={false}
                value={invertedAssignments[c.col]}
                placeholder={<FormattedMessage id="selectColumn" />}
                style={{ width: '100%' }}
                onSelect={(v: AssignableKeys) =>
                  dispatch(setAssignmentAction([sheetName, v, c.col]))
                }
              >
                {targetColumns}
              </Select>
            ),
          },
        ]}
        pagination={false}
        scroll={{ x: true }}
      />
    </>
  )
}

export default AssignColumnsToSheet
